import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router';

import { Reddit, Twitter } from '@mui/icons-material';
import { SvgIcon } from '@mui/material';

import IconEmail from 'app/ui-kit/icons/EyeOpen.svg';
import IconVirus from 'app/ui-kit/icons/Virus.svg';
import { emptyArray } from 'main/components/SearchBar/constants';
import ProtectedChip from 'pages/item/v2/components/ProtectedChip';
import { isProtectedData } from 'pages/item/v2/components/ProtectedData';
import MoreTagsChip from 'pages/item/v2/components/Tags/components/MoreTagsChip';
import isEmpty from 'utils/is-empty';
import Utils from 'utils/Utils';

import useStyles from './styles';
import { Data } from './types';

interface Props {
    data: Data;
    className?: string;
}

const Tags: React.FC<Props> = ({
    data: {
        type,
        published,
        cwe = emptyArray,
        reporter,
        viewCount,
        href,
        bounty,
        enchantments: { twitter, reddit, exploitation, tags }
    },
    className
}) => {
    const { classes, cx } = useStyles();
    const history = useHistory();

    const otherTags = useMemo(() => {
        return (tags ?? []).reduce<{ hidden: string[]; visible: string[] }>(
            (output, tag) => {
                if (isProtectedData(tag)) {
                    output.hidden.push(tag);
                } else {
                    output.visible.push(tag);
                }
                return output;
            },
            { hidden: [], visible: [] }
        );
    }, [tags]);

    return (
        <div className={cx(classes.container, className)}>
            <div className={cx(classes.line, className)}>
                <ProtectedChip
                    data={published}
                    label={({ isProtected, content }) => (
                        <span className={classes.date}>
                            {isProtected ? 'yyyy-MM-dd' : format(new Date(`${content}`), 'yyyy-MM-dd')}
                            <span className={classes.time}>
                                {isProtected ? 'HH:mm:ss' : format(new Date(`${content}`), 'HH:mm:ss')}
                            </span>
                        </span>
                    )}
                />
                {cwe.filter(Boolean).map((cwe, index) => {
                    const cweId = cwe.replace(/\D+/g, '');
                    const url = `https://cwe.mitre.org/data/definitions/${cweId}.html`;
                    return <ProtectedChip key={index} data={cwe} label={cwe} onClick={() => Utils.openUrl(url)} />;
                })}
                {reporter ? (
                    reporter.toLowerCase() === type ? null : (
                        <ProtectedChip
                            tooltip={'Reporter'}
                            data={reporter}
                            label={({ content }) => (Utils.isMobile() ? Utils.trunc(32, `${content}`) : content)}
                            onClick={() => history.push(`/search?query=reporter:"${reporter}"`)}
                        />
                    )
                ) : null}
                {href ? (
                    <ProtectedChip
                        tooltip={'Source'}
                        data={href}
                        label={({ isProtected, content }) =>
                            isProtected
                                ? content
                                : `${content}`.startsWith('http')
                                ? new URL(`${content}`).host
                                : 'link'
                        }
                        onClick={() => Utils.openUrl(href)}
                    />
                ) : null}
                {bounty ? (
                    <ProtectedChip
                        data={bounty}
                        tooltip="Bounty Amount"
                        label={({ isProtected, content }) => (isProtected ? content : `$${bounty}`)}
                    />
                ) : null}
                {viewCount ? (
                    <ProtectedChip
                        tooltip="View Count"
                        data={viewCount}
                        label={viewCount}
                        icon={<SvgIcon className={classes.tagIcon} component={IconEmail} />}
                    />
                ) : null}
                {exploitation?.wildExploited ? (
                    <ProtectedChip
                        tooltip="Exploited in the wild"
                        data={exploitation.wildExploited}
                        label="In Wild"
                        icon={<SvgIcon className={classes.tagIcon} component={IconVirus} />}
                        onClick={() =>
                            history.push(
                                '/search?query='.concat(
                                    exploitation.wildExploitedSources
                                        .map(({ idList }) => idList.map((id) => `"${id}"`))
                                        .flat()
                                        .join(' OR ')
                                )
                            )
                        }
                    />
                ) : null}
                {twitter?.counter ? (
                    <ProtectedChip
                        data={twitter.counter}
                        label={twitter.counter}
                        icon={<Twitter className={classes.tagIcon} />}
                    />
                ) : null}
                {reddit?.counter ? (
                    <ProtectedChip
                        data={reddit.counter}
                        label={reddit.counter}
                        icon={<Reddit className={classes.tagIcon} />}
                    />
                ) : null}
                {isEmpty(otherTags.hidden) ? null : isEmpty(otherTags.visible) ? (
                    <MoreTagsChip count={otherTags.hidden.length} />
                ) : null}
            </div>
            {isEmpty(otherTags.visible) ? null : (
                <div className={classes.line}>
                    {otherTags.visible.map((tag, index) => (
                        <ProtectedChip
                            key={index}
                            data={tag}
                            label={tag}
                            onClick={() => history.push(`/search?query=enchantments.tags:"${encodeURIComponent(tag)}"`)}
                        />
                    ))}
                    {isEmpty(otherTags.hidden) ? null : <MoreTagsChip count={otherTags.hidden.length} />}
                </div>
            )}
        </div>
    );
};

export default Tags;
